import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Trophy,
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  Package,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Filter,
  Download,
  ChevronRight,
  Star,
  Shield,
  Target,
  BarChart3
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Rank Badge Component
const RankBadge = ({ rank }) => {
  if (rank === 1) {
    return (
      <div className="w-8 h-8 rounded-full bg-yellow-400 flex items-center justify-center">
        <Trophy className="w-4 h-4 text-yellow-900" />
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
        <span className="text-sm font-bold text-gray-700">2</span>
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="w-8 h-8 rounded-full bg-orange-300 flex items-center justify-center">
        <span className="text-sm font-bold text-orange-800">3</span>
      </div>
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
      <span className="text-sm font-medium text-gray-600">{rank}</span>
    </div>
  );
};

// Band Badge Component  
const BandBadge = ({ band, color }) => {
  const colorClasses = {
    green: 'bg-green-100 text-green-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    orange: 'bg-orange-100 text-orange-700',
    red: 'bg-red-100 text-red-700'
  };

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${colorClasses[color] || 'bg-gray-100 text-gray-700'}`}>
      {band === 'Star' && '⭐ '}{band}
    </span>
  );
};

// Summary Card Component
const SummaryCard = ({ icon: Icon, label, value, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500',
    purple: 'bg-purple-500'
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm">
      <div className={`w-10 h-10 ${colorClasses[color]} rounded-lg flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
};

// Engineer Row Component
const EngineerRow = ({ engineer, onClick }) => (
  <div 
    onClick={onClick}
    className="bg-white rounded-xl p-4 shadow-sm mb-3 cursor-pointer hover:shadow-md transition-shadow"
  >
    <div className="flex items-center gap-3">
      <RankBadge rank={engineer.rank} />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-800 truncate">{engineer.name}</h3>
          <BandBadge band={engineer.band} color={engineer.band_color} />
        </div>
        <p className="text-sm text-gray-500">{engineer.engineer_id}</p>
      </div>
      
      <div className="text-right">
        <p className="text-xl font-bold text-[#2b3380]">{Math.round(engineer.kpu_score)}</p>
        <p className="text-xs text-gray-500">KPU</p>
      </div>
      
      <ChevronRight className="w-5 h-5 text-gray-300" />
    </div>
    
    {/* Quick Stats */}
    <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-gray-100">
      <div className="text-center">
        <p className="text-sm font-semibold text-gray-800">{engineer.pickups}</p>
        <p className="text-xs text-gray-500">Pickups</p>
      </div>
      <div className="text-center">
        <p className="text-sm font-semibold text-gray-800">₹{(engineer.net_value / 1000).toFixed(0)}K</p>
        <p className="text-xs text-gray-500">Value</p>
      </div>
      <div className="text-center">
        <p className={`text-sm font-semibold ${engineer.qc_accuracy >= 95 ? 'text-green-600' : 'text-orange-600'}`}>
          {engineer.qc_accuracy}%
        </p>
        <p className="text-xs text-gray-500">QC</p>
      </div>
      <div className="text-center">
        <p className={`text-sm font-semibold ${engineer.incentive_eligible ? 'text-green-600' : 'text-red-600'}`}>
          ₹{(engineer.net_incentive / 1000).toFixed(1)}K
        </p>
        <p className="text-xs text-gray-500">Incentive</p>
      </div>
    </div>
    
    {/* Risk Flags Warning */}
    {engineer.risk_flags > 0 && (
      <div className="mt-2 flex items-center gap-1 text-red-600">
        <AlertTriangle className="w-3 h-3" />
        <span className="text-xs">{engineer.risk_flags} risk flag{engineer.risk_flags > 1 ? 's' : ''}</span>
      </div>
    )}
  </div>
);

// Main Leaderboard Component
const EngineerLeaderboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filterBand, setFilterBand] = useState('all');

  useEffect(() => {
    fetchLeaderboard();
  }, [period]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API}/api/engineer-performance/leaderboard?period=${period}&limit=50`
      );
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        setError('Failed to load leaderboard');
      }
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchLeaderboard();
  };

  const filteredEngineers = data?.leaderboard?.filter(eng => 
    filterBand === 'all' || eng.band.toLowerCase() === filterBand.toLowerCase()
  ) || [];

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2b3380] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  const { totals } = data || {};

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#2b3380] to-[#4a5cc5] text-white px-4 pt-12 pb-8">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <button 
            onClick={handleRefresh}
            className="p-2 bg-white/10 rounded-full"
            disabled={refreshing}
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        <div className="flex items-center gap-3 mb-2">
          <Trophy className="w-8 h-8 text-yellow-400" />
          <div>
            <h1 className="text-xl font-bold">Engineer Leaderboard</h1>
            <p className="text-blue-200 text-sm">KPU Performance Rankings</p>
          </div>
        </div>
        
        {/* Period Selector */}
        <div className="flex gap-2 mt-4">
          {['day', 'week', 'month'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                period === p 
                  ? 'bg-white text-[#2b3380]' 
                  : 'bg-white/10 text-white'
              }`}
            >
              {p === 'day' ? 'Today' : p === 'week' ? 'This Week' : 'This Month'}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 -mt-4 space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <SummaryCard
            icon={Users}
            label="Total Engineers"
            value={totals?.total_engineers || 0}
            color="blue"
          />
          <SummaryCard
            icon={Package}
            label="Total Pickups"
            value={totals?.total_pickups || 0}
            color="green"
          />
          <SummaryCard
            icon={DollarSign}
            label="Total Value"
            value={`₹${((totals?.total_value || 0) / 100000).toFixed(1)}L`}
            color="purple"
          />
          <SummaryCard
            icon={Target}
            label="Avg KPU"
            value={totals?.avg_kpu || 0}
            color="orange"
          />
        </div>

        {/* Band Distribution */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-3">Performance Distribution</h3>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterBand('all')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                filterBand === 'all' ? 'bg-[#2b3380] text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              All ({data?.leaderboard?.length || 0})
            </button>
            <button
              onClick={() => setFilterBand('star')}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                filterBand === 'star' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700'
              }`}
            >
              ⭐ {totals?.star_count || 0}
            </button>
            <button
              onClick={() => setFilterBand('stable')}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                filterBand === 'stable' ? 'bg-yellow-600 text-white' : 'bg-yellow-50 text-yellow-700'
              }`}
            >
              🟡 {totals?.stable_count || 0}
            </button>
            <button
              onClick={() => setFilterBand('risk')}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                filterBand === 'risk' ? 'bg-orange-600 text-white' : 'bg-orange-50 text-orange-700'
              }`}
            >
              🟠 {totals?.risk_count || 0}
            </button>
            <button
              onClick={() => setFilterBand('unsafe')}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                filterBand === 'unsafe' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700'
              }`}
            >
              🔴 {totals?.unsafe_count || 0}
            </button>
          </div>
        </div>

        {/* Leaderboard List */}
        <div>
          <h3 className="font-semibold text-gray-800 mb-3">
            Rankings
            {filterBand !== 'all' && ` - ${filterBand.charAt(0).toUpperCase() + filterBand.slice(1)} Band`}
          </h3>
          
          {filteredEngineers.length === 0 ? (
            <div className="bg-white rounded-xl p-8 shadow-sm text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No engineers found</p>
            </div>
          ) : (
            filteredEngineers.map((engineer) => (
              <EngineerRow 
                key={engineer.engineer_id}
                engineer={engineer}
                onClick={() => navigate(`/admin/engineer-performance/${engineer.engineer_id}`)}
              />
            ))
          )}
        </div>

        {/* Legend */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-3">KPU Score Bands</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span>Star (85-100)</span>
              </div>
              <span className="text-gray-500">Full incentive + scale</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <span>Stable (70-84)</span>
              </div>
              <span className="text-gray-500">Incentive with coaching</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                <span>Risk (50-69)</span>
              </div>
              <span className="text-gray-500">Partial payout</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span>Unsafe (0-49)</span>
              </div>
              <span className="text-gray-500">No incentive + corrective</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngineerLeaderboard;
